import React from 'react';
import { withRouter } from 'react-router';
import { cellTypes } from 'core/table';
import { replaceInArray } from 'utils/helpers';
import noop from 'utils/noop';
import { FlexSpacer } from 'ui/flexSpacer';
import { Tooltip } from 'ui/tooltip';
import { PrimaryButton, SecondaryButton, BUTTON_SIZES } from 'ui/button';
import { get, find, isEmpty } from 'lodash';
import CircleCheckmarkCell from './CircleCheckmarkCell';
import MemberNameEmail from './MemberNameEmail';
import RelativeDateWithHover from './RelativeDateWithHover';
import { RolesDropdown } from './RolesDropdown';
import { StyledTable } from './Organization.styled';

// type Props = {
//   canManageProject: ?() => Boolean,
//   members: [],
//   isLoading: boolean,
//   onChangeMembers: (updatedMembers: []) => {},
//   project: Project,
//   combinedRoleOptions: [],
//   projectRoleOptions: [],
//   teamRoleOptions: [],
//   projects: Project[],
//   onSave: ?() => {},
// };
export class ProjectMembers extends React.Component {
  static defaultProps = {
    onChangeMembers: noop,
    projectRoleOptions: [],
  };

  constructor(props) {
    super(props);
    this.state = {
      selectedRows: [],
      updatedMembers: {},
      members: props.members || [],
      project: props.project,
    };
  }

  UNSAFE_componentWillReceiveProps(nextProps) {
    const { members } = nextProps;
    if (isEmpty(this.state.members) && !isEmpty(members)) {
      this.setState({ members });
    }
  }

  /**
   * handleSelectAllRows
   * Selects all the rows in the table.
   * @returns { void }
   */
  handleSelectAllRows = () => {
    const { selectedRows } = this.state;
    const { members } = this.props;

    this.setState({
      selectedRows: selectedRows.length === members.length ? [] : members.map((row) => row.userId),
    });
  };

  /**
   * handleSelectRow
   * Sets the current selected row.
   * @param { Object } e: The click event
   * @param { Object } updatedValues: The updated values containing the updated selected rows.
   * @returns { void }
   */
  handleSelectRow = (e, { selectedRows }) => {
    this.setState({ selectedRows });
  };

  /**
   * handleUpdateMembers
   * Helper function to update the members list with a change to a single member.
   * @param { string } userId: The userId of the member to update.
   * @param { Object } updatedValues: The updated values to change of the member.
   * @returns { void }
   */
  handleUpdateMembers = (userId, updatedValues) => {
    const { members } = this.state;
    const { onChangeMembers } = this.props;
    const memberIndex = members.findIndex((member) => member.userId === userId);
    const member = members[memberIndex];
    const updatedMember = { ...member, ...updatedValues };
    const updatedMembers = replaceInArray(members, memberIndex, updatedMember);
    onChangeMembers(updatedMembers);

    this.setState({
      members: updatedMembers,
      updatedMembers: {
        ...this.state.updatedMembers,
        [userId]: updatedMember,
      },
    });
  };

  /**
   * handleClick
   * Callback function when checkbox of member row is checked.
   * @param { Object } e: The change event
   * @param { Object } updatedValues: The updated values with updated checked value and rowData.
   * @returns { void }
   */

  handleClick = (e, { checked, rowData }) => {
    const { userId } = rowData;
    const { members } = this.state;
    const { project } = this.props;

    const foundIndex = members.findIndex((m) => m.userId === userId);
    const updatedMember = members[foundIndex];

    const projectMemberships = checked
      ? [
          ...updatedMember.projectMemberships,
          {
            projectId: project.id,
          },
        ]
      : updatedMember.projectMemberships.filter((mship) => mship.projectId !== project.id);

    const teamMemberships = checked
      ? [
          ...updatedMember.teamMemberships,
          {
            projectId: project.id,
            teamId: project.rootTeamId,
          },
        ]
      : updatedMember.teamMemberships.filter((mship) => mship.projectId !== project.id);

    const projectRole = checked ? updatedMember.projectRole : '';
    const teamRole = checked ? updatedMember.teamRole : '';

    const newMember = {
      ...updatedMember,
      projectMemberships,
      teamMemberships,
      projectRole,
      teamRole,
    };

    this.setState(
      {
        members: replaceInArray(members, foundIndex, newMember),
      },

      () => this.handleUpdateMembers(userId, { inProject: checked }),
    );
  };

  /**
   * handleChangeProjectRole
   * Callback function when project role dropdown value is changed.
   * @param { Object } e: The change event
   * @param { Object } updatedRole: The updated role values with updated projectRole value and rowData.
   * @returns { void }
   */
  handleChangeProjectRole = (rowData) => (event, updatedRole) => {
    const { value, roles } = updatedRole;
    const { userId } = rowData;
    const roleId = roles.find((r) => r.name === value).id;
    const { members } = this.state;
    const foundIndex = members.findIndex((m) => m.userId === userId);
    const updatedMember = members[foundIndex];

    const projectMemberships = updatedMember.projectMemberships.map((mship) => {
      if (mship.projectId === this.props.project.id) {
        return {
          ...mship,
          roleId,
        };
      }
      return mship;
    });

    const newMember = {
      ...updatedMember,
      projectMemberships,
    };

    this.setState(
      {
        members: replaceInArray(members, foundIndex, newMember),
      },
      () => this.handleUpdateMembers(userId, { projectRole: value }),
    );
  };

  /**
   * handleChangeTeamRole
   * Callback function when team role dropdown value is changed.
   * @param { Object } e: The change event
   * @param { Object } updatedRole: The updated role values with updated teamRole value and rowData.
   * @returns { void }
   */
  handleChangeTeamRole = (rowData) => (event, updatedRole) => {
    const { value, roles } = updatedRole;
    const { userId } = rowData;
    const roleId = roles.find((r) => r.name === value).id;
    const { members } = this.state;
    const foundIndex = members.findIndex((m) => m.userId === userId);
    const updatedMember = members[foundIndex];

    const teamMemberships = updatedMember.teamMemberships.map((mship) => {
      if (mship.projectId === this.props.project.id) {
        return {
          ...mship,
          roleId,
        };
      }
      return mship;
    });

    const newMember = {
      ...updatedMember,
      teamMemberships,
    };

    this.setState(
      {
        members: replaceInArray(members, foundIndex, newMember),
      },
      () => this.handleUpdateMembers(userId, { teamRole: value }),
    );
  };

  handleChangeProjectAndTeamRole = (rowData) => (value, projectRoles) => {
    const { userId } = rowData;
    const projectRoleId = projectRoles.find((r) => r.name === value).id;
    const teamRoleId = this.props.teamRoleOptions.find((option) => option.name === value).id;
    const { members } = this.state;
    const foundIndex = members.findIndex((m) => m.userId === userId);
    const updatedMember = members[foundIndex];

    const projectMemberships = updatedMember.projectMemberships.map((mship) => {
      if (mship.projectId === this.props.project.id) {
        return {
          ...mship,
          roleId: projectRoleId,
        };
      }
      return mship;
    });

    const teamMemberships = updatedMember.teamMemberships.map((mship) => {
      if (mship.projectId === this.props.project.id) {
        return {
          ...mship,
          roleId: teamRoleId,
        };
      }
      return mship;
    });

    const newMember = {
      ...updatedMember,
      projectMemberships,
      teamMemberships,
    };

    this.setState(
      {
        members: replaceInArray(members, foundIndex, newMember),
      },
      () => this.handleUpdateMembers(userId, { projectRole: value, teamRole: value }),
    );
  };

  /**
   * Removes member from specified project or team using the
   * corresponding project id by updating their membership.
   */
  removeMembership = (membership, project) =>
    membership.filter((mship) => mship.projectId !== project.id);

  /**
   * Locates id of specified project or team role.
   */
  findNewRole = (roleOptions, newRole) => roleOptions.find((r) => r.name === newRole).id;

  /**
   * Assigns member the specified project or team role.
   */
  assignNewRole = (membership, project, roleOptions, newRole) =>
    membership.map((mship) => {
      if (mship.projectId === project.id) {
        return {
          ...mship,
          roleId: this.findNewRole(roleOptions, newRole),
        };
      }
      return mship;
    });

  /**
   * bulkUpdate
   * Bulk action to modify project.
   * @param { Object } updatedProps: Updated props to change for each member.
   * @param { Array } selectedRows: The ids of the members to add/remove.
   * @returns { void }
   */
  bulkUpdate = (updatedProps, selectedRows) => {
    const { members } = this.state;
    const { combinedRoleOptions, onChangeMembers, project, teamRoleOptions } = this.props;
    const updatedMembers = members.map((member) => {
      let updatedMember = { ...member, ...updatedProps };
      if (selectedRows.includes(member.userId)) {
        if (updatedProps.inProject) {
          // bulk add
          updatedMember = {
            ...updatedMember,
            projectMemberships: [
              ...member.projectMemberships,
              {
                projectId: project.id,
              },
            ],
            teamMemberships: [
              ...member.teamMemberships,
              {
                projectId: project.id,
                teamId: project.rootTeamId,
              },
            ],
          };
        } else if (updatedProps.inProject === false) {
          // bulk remove
          updatedMember = {
            ...updatedMember,
            projectMemberships: this.removeMembership(member.projectMemberships, project),
            projectRole: '',
            teamMemberships: this.removeMembership(member.teamMemberships, project),
            teamRole: '',
          };
        } else if (!isEmpty(updatedProps.projectRole)) {
          // update project role(s)
          const roleOptions = combinedRoleOptions;
          updatedMember = {
            ...updatedMember,
            projectMemberships: this.assignNewRole(
              member.projectMemberships,
              project,
              roleOptions,
              updatedProps.projectRole,
            ),
          };
        }
        if (!isEmpty(updatedProps.teamRole)) {
          // update team role(s)
          const roleOptions = teamRoleOptions.filter((role) => role.name === updatedProps.teamRole);
          updatedMember = {
            ...updatedMember,
            teamMemberships: this.assignNewRole(
              member.teamMemberships,
              project,
              roleOptions,
              updatedProps.teamRole,
            ),
          };
        }
        return updatedMember;
      }
      return member;
    });

    onChangeMembers(updatedMembers);

    const changedMembers = { ...this.state.changedMembers };

    selectedRows.forEach((userId) => {
      changedMembers[userId] = updatedMembers.find((m) => m.userId === userId);
    });

    this.setState({ members: updatedMembers, updatedMembers: changedMembers });
  };

  /**
   * handleBulkAddToProject
   * Callback function when bulk add action is clicked
   * @param { Object } e: The click event
   * @param { Object } updatedProps: Props containing the selectedRows.
   * @returns { void }
   */
  handleBulkAddToProject = (e, { selectedRows }) => {
    this.bulkUpdate({ inProject: true }, selectedRows);
  };

  /**
   * handleBulkRemoveFromProject
   * Callback function when bulk remove action is clicked
   * @param { Object } e: The click event
   * @param { Object } updatedProps: Props containing the selectedRows.
   * @returns { void }
   */
  handleBulkRemoveFromProject = (e, { selectedRows }) => {
    this.bulkUpdate({ inProject: false }, selectedRows);
  };

  /**
   * handleBulkChangeProjectRole
   * Callback function when bulk project role action dropdown is changed.
   * @param { Object } e: The click event
   * @param { Object } updatedProps: Props containing the selectedRows and selected role value.
   * @returns { void }
   */
  handleBulkChangeProjectRole = (e, { selectedRows, value }) => {
    this.bulkUpdate({ projectRole: value }, selectedRows);
  };

  /**
   * handleBulkChangeTeamRole
   * Callback function when bulk team role action dropdown is changed.
   * @param { Object } e: The click event
   * @param { Object } updatedProps: Props containing the selectedRows and selected role value.
   * @returns { void }
   */
  handleBulkChangeTeamRole = (e, { selectedRows, value }) => {
    this.bulkUpdate({ teamRole: value }, selectedRows);
  };

  /**
   * handleBulkChangeProjectAndTeamRole
   * Callback function when bulk role action dropdown is changed (used for combined RBAC).
   * @param { Object } e: The click event
   * @param { Object } updatedProps: Props containing the selectedRows and selected role value.
   * @returns { void }
   */
  handleBulkChangeProjectandTeamRole = (e, { selectedRows, value }) => {
    this.bulkUpdate({ projectRole: value, teamRole: value }, selectedRows);
  };

  getProjectMembership = (rowData) =>
    rowData.projectMemberships.find((mship) => mship.projectId === this.props.project.id);

  getTeamMembership = (rowData) =>
    rowData.teamMemberships.find((mship) => mship.projectId === this.props.project.id);

  getProjectRoleName = (member) => {
    if (member.projectRole) return member.projectRole;

    const { project, projectRoleOptions } = this.props;

    if (project && projectRoleOptions) {
      const membership = find(member.projectMemberships, { projectId: this.props.project.id });

      if (membership) {
        return get(
          projectRoleOptions.find((r) => r.id === membership.roleId),
          'name',
          'not found',
        );
      }
    }
    return 'not found';
  };

  getTeamRoleName = (member) => {
    if (member.teamRole) return member.teamRole;

    const { project, teamRoleOptions } = this.props;

    if (project && teamRoleOptions) {
      const membership = find(member.teamMemberships, { projectId: this.props.project.id });

      if (membership) {
        return get(
          teamRoleOptions.find((r) => r.id === membership.roleId),
          'name',
          'not found',
        );
      }
    }
    return 'not found';
  };

  columns = [
    {
      name: 'Member',
      type: cellTypes.CUSTOM,
      getCellContent: ({ rowData }) => {
        const { userId, fullName, email } = rowData;
        return <MemberNameEmail userId={userId} fullName={fullName} email={email} />;
      },
    },
    {
      name: 'Belongs to project',
      type: cellTypes.TOGGLE,
      getToggleValue: (toggle, { inProject }) => inProject,
      onClick: this.handleClick,
      maxWidth: 150,
    },
    {
      name: 'Last signed in',
      type: cellTypes.CUSTOM,
      getCellContent: ({ rowData }) => {
        const { lastLoginAt } = rowData;
        return <RelativeDateWithHover date={lastLoginAt} />;
      },
      maxWidth: 150,
    },
    {
      name: 'Role',
      type: cellTypes.CUSTOM,
      getCellContent: ({ rowData }) => {
        const { combinedRoleOptions } = this.props;
        const dropdownOnChange = this.handleChangeProjectAndTeamRole(rowData);

        return (
          <RolesDropdown
            data-test="rolesDropdown"
            onChange={dropdownOnChange}
            roles={combinedRoleOptions}
            value={this.getProjectRoleName(rowData)}
            isDisabled={!this.getProjectMembership(rowData)}
          />
        );
      },
      align: 'center',
      hasDropdown: true,
      maxWidth: 250,
    },
    {
      name: 'Org admin',
      type: cellTypes.CUSTOM,
      getCellContent: ({ rowData }) => {
        const { orgAdmin } = rowData;
        return <CircleCheckmarkCell checked={orgAdmin} />;
      },
      maxWidth: 100,
      align: 'center',
    },
  ];

  saveMembers = () => {
    this.props.onSave(Object.values(this.state.updatedMembers));
  };

  saveMembersDisabled = () => {
    const { updatedMembers } = this.state;
    const { canManageProject, project } = this.props;
    const belongsToProject = canManageProject(project.id);
    const noSelectedBulkActions = isEmpty(updatedMembers);
    let tooltip = '';

    if (!belongsToProject) {
      tooltip = 'You can only manage members of the projects you belong to.';
    } else if (noSelectedBulkActions) {
      tooltip = 'You must select an action to save changes.';
    }

    const invalidMembers = Object.values(updatedMembers).filter(
      (member) =>
        member &&
        ((member.inProject && !member.projectRole) || (!member.inProject && member.projectRole)),
    );

    return {
      disabled: noSelectedBulkActions || invalidMembers.length > 0 || !belongsToProject,
      tooltip,
    };
  };

  /**
   * getProjectRoleOptionsForBulkActions
   * Formats the project role props to options for bulk actions dropdown.
   * @returns { Array } The formatted dropdown options.
   */
  getProjectRoleOptionsForBulkActions = () => {
    const { combinedRoleOptions } = this.props;
    const roleOptions = combinedRoleOptions;
    return roleOptions.map(({ name }) => ({ value: name, text: name }));
  };

  /**
   * getTeamRoleOptionsForBulkActions
   * Formats the team role props to options for bulk actions dropdown.
   * @returns { Array } The formatted dropdown options.
   */
  getTeamRoleOptionsForBulkActions = () =>
    this.props.teamRoleOptions.map(({ name }) => ({ value: name, text: name }));

  resetChanges = () =>
    this.setState({
      members: this.props.members,
      updatedMembers: {},
      selectedRows: [],
    });

  render() {
    const { members, selectedRows } = this.state;

    const bulkActions = [
      {
        label: 'Add to project',
        onClick: this.handleBulkAddToProject,
      },
      {
        label: 'Remove from project',
        onClick: this.handleBulkRemoveFromProject,
      },
      {
        label: 'Select role',
        onClick: this.handleBulkChangeProjectandTeamRole,
        options: this.getProjectRoleOptionsForBulkActions(),
      },
    ];

    return (
      <FlexSpacer flexDirection="column" alignItems="stretch">
        <FlexSpacer justifyContent="flex-end">
          <SecondaryButton size={BUTTON_SIZES.SMALL} onClick={this.resetChanges}>
            Cancel
          </SecondaryButton>
          <Tooltip
            isEnabled={
              !isEmpty(this.saveMembersDisabled().tooltip)
                ? true
                : this.saveMembersDisabled().disabled
            }
            tooltipContent={this.saveMembersDisabled().tooltip}
          >
            <PrimaryButton
              data-test="saveActionButton"
              isDisabled={this.saveMembersDisabled().disabled}
              onClick={this.saveMembers}
              size={BUTTON_SIZES.SMALL}
            >
              Save changes
            </PrimaryButton>
          </Tooltip>
        </FlexSpacer>
        <StyledTable
          appearance={'normal'}
          bulkActions={bulkActions}
          columns={this.columns}
          rows={members}
          itemName={'member'}
          itemNamePlural={'members'}
          onSelectAllRows={this.handleSelectAllRows}
          onSelectRow={this.handleSelectRow}
          selectedRows={selectedRows}
          selectableRows
          hidePagination
          total={members.length}
        />
      </FlexSpacer>
    );
  }
}

export default withRouter(ProjectMembers);
